import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import en from './locales/en.json'
import fr from './locales/fr.json'
import ar from './locales/ar.json'

export const languages = [
  { code: 'en', name: 'English', dir: 'ltr' },
  { code: 'fr', name: 'Français', dir: 'ltr' },
  { code: 'ar', name: 'العربية', dir: 'rtl' },
] as const

export type LanguageCode = (typeof languages)[number]['code']

const STORAGE_KEY = 'i18nextLng'

// Get supported language codes
const supportedLanguages = languages.map(l => l.code)

// Check if a language code is supported (handles variants like 'en-US' -> 'en')
const getSupportedLanguage = (lang: string | null): string | null => {
  if (!lang) return null
  // Direct match
  if (supportedLanguages.includes(lang as LanguageCode)) return lang
  // Try base language (e.g., 'en-US' -> 'en')
  const baseLang = lang.split('-')[0]
  if (supportedLanguages.includes(baseLang as LanguageCode)) return baseLang
  return null
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
      ar: { translation: ar },
    },
    fallbackLng: 'en',
    supportedLngs: supportedLanguages,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
      convertDetectedLanguage: (lng) => getSupportedLanguage(lng) || 'en',
    },
  })

// Update document direction and lang attribute when language changes
// Note: localStorage persistence is handled automatically by LanguageDetector
i18n.on('languageChanged', (lng) => {
  const language = languages.find(l => l.code === lng)
  if (language) {
    document.documentElement.dir = language.dir
    document.documentElement.lang = lng
  }
})

// Set initial direction
const currentLang = languages.find(l => l.code === i18n.language)
if (currentLang) {
  document.documentElement.dir = currentLang.dir
  document.documentElement.lang = i18n.language
}

export default i18n
